var app = angular.module('leonardo');

/**
 * TaskDynamicFormExpoDialog - Angular controller for the Expo dynamic form dialog.
 * This controller is responsible for:
 * 1. Loading the Expo app in an iframe
 * 2. Passing initialization data to the Expo app
 * 3. Handling messages from the Expo app
 * 4. Dialog management (close, confirm, etc.)
 * 
 * Note: All API calls related to dynamic form data are now handled directly
 * by the Expo app for simplicity and better architectural separation.
 */
var TaskDynamicFormExpoDialog = function (
  $scope,
  $q,
  $sce,
  formObj,
  formDataObj,
  FilterService,
  PropertyService,
  AuthDataService,
  AuthService,
  UnitService,
  $timeout,
  ngDialog,
  DynamicFormDataRestService,
  StatsigService
) {
  // Construct URL to Expo app
  var expoAppBase = '/react-native-web/index.html';
  $scope.expoAppUrl = $sce.trustAsResourceUrl(expoAppBase);
  
  // UI state
  $scope.loading = true;
  $scope.errorMessage = null;
  $scope.canSubmit = false;
  $scope.formInProgress = false;
  
  // Guard flag to prevent duplicate initialization
  var initializationHandled = false;

  // Guard flag to ensure logout is only triggered once per dialog session
  var sessionExpiredHandled = false;

  /**
   * Handle messages from the Expo app
   */
  function handleMessage(event) {
    var message = event.data;

    if (!message || !message.type) {
      return;
    }
    
    $scope.$apply(function() {
      switch(message.type) {
        case 'EXPO_INITIALIZED':
          handleExpoInitialized();
          break;
          
        case 'EXPO_FORM_SUBMIT':
          handleFormSubmit(message.payload);
          break;
          
        case 'EXPO_FORM_SKIP':
          $scope.confirm('skip');
          break;
          
        case 'EXPO_FORM_CLOSE':
          $scope.closeThisDialog({
            df_attachments_note: $scope.df_attachments_note, 
            df_comments: $scope.ngDialogData.df_comments,
            formInProgress: $scope.formInProgress
          });
          break;
          
        case 'EXPO_FORM_IN_PROGRESS':
          if (message.payload && typeof message.payload.in_progress === 'boolean') {
            $scope.formInProgress = message.payload.in_progress;
          }
          break;
          
        case 'EXPO_ERROR':
          showError(message.payload && message.payload.message ? message.payload.message : 'Unknown error');
          break;

        case 'EXPO_SESSION_EXPIRED':
          handleSessionExpired();
          break;
          
        default:
          break;
      }
    });
  }

  /**
   * Handle session expiration reported by the Expo app.
   * Triggers a single logout so the user is redirected to the login page.
   */
  function handleSessionExpired() {
    if (sessionExpiredHandled) {
      return;
    }
    sessionExpiredHandled = true;

    console.warn('[TaskDynamicFormExpoDialog] EXPO_SESSION_EXPIRED received — logging out');

    // Pass true to skip the backend logout call; the token is already invalid.
    AuthService.logout(true);
  }
  
  /**
   * Handle Expo initialization
   * 
   * This function is called when the Expo app signals it's initialized.
   * Sends initialization data to the Expo app including:
   * - Form definition and data
   * - Task information
   * - JWT token (for direct API calls from Expo)
   * 
   * If dynamicFormDataId is missing, it will create a new dynamic_form_data record first.
   */
  async function handleExpoInitialized() {
    // Prevent duplicate initialization calls
    if (initializationHandled) {
      console.log('[TaskDynamicFormExpoDialog] Ignoring duplicate EXPO_INITIALIZED message');
      return;
    }
    // Check if we're in an iframe
    var inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch (e) {
      inIframe = true;
    }

    // Extract dynamic form data ID
    var dynamicFormDataId = formDataObj ? formDataObj.id : null;
    
    // Get dynamic_form ID for Statsig gate check
    var dynamicFormId = formObj ? formObj.id : null;
    
    var gateEnabledForExpo = true; // Default to enabled
    if(inIframe) {
      gateEnabledForExpo = await StatsigService.checkGate('envoy_dynamic_forms', { dynamic_form_id: dynamicFormId });
    }

      // If in iframe and gate is disabled, show feature gate dialog over modal
    if (inIframe && !gateEnabledForExpo) {
      ngDialog.open({
        template: 'views/templates/dialog/feature-gate.html',
        className: 'ngdialog-theme-default ngdialog-feature-gate',
        controller: 'FeatureGateDialog',
        data: {
          parentScope: $scope
        }
      });
      // continue initialization to show the modal in background
    }
    
    // If dynamicFormDataId is missing, create it before initializing the Expo app
    if (!dynamicFormDataId) {
      // Set flag immediately to prevent duplicate API calls while async operation is in progress
      initializationHandled = true;
      
      createDynamicFormData().then(function(newFormData) {
        dynamicFormDataId = newFormData.id;
        // Update the task object with the new dynamic_form_data ID
        if ($scope.task) {
          $scope.task.dynamic_form_data = newFormData;
        }
        sendInitMessage(dynamicFormDataId);
      }).catch(function(error) {
        console.error('[TaskDynamicFormExpoDialog] Failed to create dynamic_form_data:', error);
        // Reset flag to allow retry on failure
        initializationHandled = false;
        showError('Failed to initialize form. Please try again.');
      });
    } else {
      // No async operation needed, mark as handled immediately
      initializationHandled = true;
      sendInitMessage(dynamicFormDataId);
    }
  }
  
  /**
   * Create a new dynamic_form_data record
   * This matches the mobile app implementation for consistency
   * @returns {Promise} Promise that resolves with the created form data
   */
  function createDynamicFormData() {
    var actionTaskId = $scope.task ? $scope.task.id : null;
    
    // Get dynamic_form_id from task object (matching mobile implementation)
    var dynamicFormId = null;
    if ($scope.task && $scope.task.dynamic_form) {
      dynamicFormId = typeof $scope.task.dynamic_form === 'object' 
        ? $scope.task.dynamic_form.id 
        : $scope.task.dynamic_form;
    }
    
    // Check required fields
    if (!actionTaskId || !dynamicFormId) {
      return $q.reject(new Error('Cannot create dynamic_form_data: action_item_id and dynamic_form are required'));
    }
    
    // Generate UUID for this form data (simple implementation)
    var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    
    // Match mobile app payload structure exactly
    var payload = {
      action_item_id: actionTaskId,
      dynamic_form: dynamicFormId,
      in_progress: true,
      data: {},
      uuid: uuid,
      dynamic_form_fields: [], // Required field - empty for initial creation
      dynamic_form_fields_group_ids: '' // Optional but good to include
    };
    return DynamicFormDataRestService.post(payload);
  }
  
  /**
   * Send the INIT message to the Expo app
   * @param {number|null} dynamicFormDataId - The dynamic form data ID
   */
  function sendInitMessage(dynamicFormDataId) {
    var dataToSend = {
      // Form and task data
      envoyTaskId: _.get(formDataObj, 'action_item.v3_task_id', null),
      actionTaskId: $scope.task.id,
      dynamicFormDataId: dynamicFormDataId,
      workOrdersEnabled: $scope.ngDialogData.workOrdersEnabled || false,
      
      // Approval-specific flags
      approvalStatus: $scope.ngDialogData.approvalStatus || false,
      showClose: $scope.ngDialogData.showClose !== false, // default to true unless explicitly false
      
      // API connectivity
      baseUrl: window.LEO_BASE_URL,
      jwtToken: localStorage.getItem('jwt_token'),
      
      // User information
      userInfo: {
        id: AuthDataService.authData.user.id,
        email: AuthDataService.authData.user.email,
        first_name: AuthDataService.authData.user.first_name,
        last_name: AuthDataService.authData.user.last_name,
        name: AuthDataService.authData.user.last_name 
          ? [AuthDataService.authData.user.first_name || '', AuthDataService.authData.user.last_name].join(' ')
          : AuthDataService.authData.user.email
      },
      
      // Task information
      task: {
        id: $scope.task.id,
        name: $scope.task.name,
        due_date: $scope.task.due_date,
        priority: $scope.task.priority,
        status: $scope.task.status,
        property_id: $scope.task.property || _.get($scope.task, 'workflow_instance.unit.property') || null,
        unit_id: $scope.task.unit_id || _.get($scope.task, 'workflow_instance.unit.id') || null,
        workflow_instance: $scope.task.workflow_instance,
        users_assigned: ($scope.task.users_assigned || []).map(function(userAssigned) {
          if (userAssigned && userAssigned.user) {
            return _.pick(userAssigned.user, [
              'id',
              'email',
              'first_name',
              'last_name',
              'title'
            ]);
          }
          return userAssigned;
        })
      },
    };

    // Send INIT message with dynamic-form mode
    sendMessageToExpo('INIT', JSON.parse(JSON.stringify(dataToSend)), 'dynamic-form');

    $scope.loading = false;
    $scope.canSubmit = true;
  }
  
  /**
   * Handle form submission from Expo
   * 
   * This function receives data from the Expo app and handles dialog actions.
   * The Expo app is now responsible for all API calls to save form data.
   * This controller only needs to handle dialog control (close/confirm) actions.
   */
  function handleFormSubmit(payload) {
    var action = payload.in_progress ? 'save' : 'submit';
    var formData = payload;
    
    if (!formData) {
      showError('Invalid form data received');
      return;
    }
    
    switch (action) {
      case 'skip':
        $scope.confirm('skip');
        break;
        
      case 'close':
        $scope.closeThisDialog({
          df_attachments_note: $scope.df_attachments_note, 
          df_comments: $scope.ngDialogData.df_comments 
        });
        break;
        
      default:
        var submissionData = { 
          dynamic_form_data: formData, 
          task: $scope.task.id, 
          df_attachments_note: $scope.df_attachments_note, 
          df_comments: $scope.ngDialogData.df_comments 
        };
        
        $scope.confirm(submissionData);
        break;
    }
  }
  
  /**
   * Send a message to the Expo app iframe
   * 
   * @param {string} type - Message type identifier
   * @param {object} payload - Message payload data
   * @param {string} mode - Optional mode parameter (e.g., 'work-order', 'dynamic-form')
   */
  function sendMessageToExpo(type, payload, mode) {
    var iframe = document.getElementById('expo-app-iframe');
    if (!iframe) {
      console.warn('[TaskDynamicFormExpoDialog] Iframe not found');
      return;
    }
    
    var message = {
      type: type,
      payload: payload || {}
    };
    
    // Add mode parameter for INIT messages
    if (mode) {
      message.mode = mode;
    }
    
    console.log('[TaskDynamicFormExpoDialog] Sending message:', message.type, mode ? '(mode: ' + mode + ')' : '');
    iframe.contentWindow.postMessage(message, '*');
  }
  
  /**
   * Show error message to the user
   * 
   * @param {string} message - Error message to display
   */
  function showError(message) {
    $scope.loading = false;
    $scope.errorMessage = message;
    
    // Show error dialog
    $timeout(function() {
      ngDialog.open({
        template: 'views/templates/dialog/error-message.html',
        className: 'ngdialog-theme-default',
        data: { message: message }
      });
    }, 500);
  }
  
  /**
   * Initialize the controller
   */
  function init() {
    window.addEventListener('message', handleMessage);

    $scope.$on('ngDialog.opened', function (e, $dialog) {
      $scope.$apply(function() {
        $scope.loading = false;
      });
    });

    $scope.promises = [$scope.ngDialogData.task];

    $q.all($scope.promises).then(function (results) {
      $scope.task = results[0];
    });
  }
  
  $scope.$on('$destroy', function () {
    window.removeEventListener('message', handleMessage);
  });
  
  init();
};

app.controller('TaskDynamicFormExpoDialog', [
  '$scope',
  '$q',
  '$sce',
  'formObj',
  'formDataObj',
  'FilterService',
  'PropertyService',
  'AuthDataService',
  'AuthService',
  'UnitService',
  '$timeout',
  'ngDialog',
  'DynamicFormDataRestService',
  'StatsigService',
  TaskDynamicFormExpoDialog
]);
